import { createTheme } from "@mui/material/styles";

export function createWidgetTheme(options = {}) {
  const {
    primaryColor = "#48C9B0",
    secondaryColor = "#117A65",
    fontFamily = "'Manrope', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
  } = options;

  return createTheme({
    palette: {
      primary: {
        main: primaryColor,
        light: lightenColor(primaryColor, 0.2),
        dark: darkenColor(primaryColor, 0.2),
        contrastText: "#ffffff"
      },
      secondary: {
        main: secondaryColor,
        contrastText: "#ffffff"
      },
      background: {
        // Near-white wash of the brand color (matches the old #f5fbfa for the
        // default teal) so custom-branded widgets tint their canvas to match.
        default: lightenColor(primaryColor, 0.94),
        paper: "#ffffff"
      },
      text: {
        primary: "#1a1a1a",
        secondary: "#666666"
      },
      success: {
        main: "#4caf50",
        light: "#81c784"
      },
      warning: {
        main: "#ff9800",
        light: "#ffb74d"
      },
      error: {
        main: "#f44336",
        light: "#e57373"
      },
      // A SETTLED invoice owes nothing but was never paid directly — it was
      // consolidated into another invoice that was. Green would make it a
      // duplicate of PAID, and the five semantic slots above plus MUI's info
      // blue are all spoken for by other invoice states, so it gets its own.
      settled: {
        main: "#7e57c2",
        light: "#b39ddb",
        contrastText: "#ffffff"
      }
    },
    typography: {
      fontFamily,
      h1: {
        fontSize: "2rem",
        fontWeight: 600,
        lineHeight: 1.2
      },
      h2: {
        fontSize: "1.5rem",
        fontWeight: 600,
        lineHeight: 1.3
      },
      h3: {
        fontSize: "1.25rem",
        fontWeight: 600,
        lineHeight: 1.4
      },
      h4: {
        fontSize: "1.125rem",
        fontWeight: 500,
        lineHeight: 1.4
      },
      body1: {
        fontSize: "1rem",
        lineHeight: 1.5
      },
      body2: {
        fontSize: "0.875rem",
        lineHeight: 1.5
      },
      button: {
        textTransform: "none",
        fontWeight: 500
      }
    },
    // 0.5rem == MUI's default 8px step, so every p/m/gap/px/py in the app is
    // byte-identical at a 16px root but scales with the user's font-size setting.
    // shape.borderRadius must stay a NUMBER: MUI resolves `borderRadius: 2` by
    // multiplying it, and a string here makes every numeric borderRadius resolve
    // to undefined.
    spacing: (factor) => `${0.5 * factor}rem`,
    shape: {
      borderRadius: 8
    },
    components: {
      MuiButton: {
        styleOverrides: {
          // Padding is per size, not on root: a root padding beats MUI's own
          // sizeSmall/sizeLarge rules, which made every small button render at
          // medium size. sizeMedium keeps the original value.
          root: {
            borderRadius: 8,
            fontWeight: 500
          },
          sizeSmall: {
            padding: "0.375rem 0.875rem"
          },
          sizeMedium: {
            padding: "0.625rem 1.25rem"
          },
          sizeLarge: {
            padding: "0.75rem 1.5rem"
          },
          contained: {
            boxShadow: "none",
            "&:hover": {
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)"
            }
          }
        }
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            boxShadow: "0 4px 20px rgba(26,26,26,0.04)",
            backgroundImage: "none"
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none"
          }
        }
      },
      MuiTextField: {
        defaultProps: {
          variant: "outlined",
          size: "small"
        }
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 6
          }
        }
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 500,
            minWidth: "auto",
            padding: "0.75rem 1rem"
          }
        }
      }
    }
  });
}

function lightenColor(hex, amount) {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;

  const r = Math.min(255, Math.round(rgb.r + (255 - rgb.r) * amount));
  const g = Math.min(255, Math.round(rgb.g + (255 - rgb.g) * amount));
  const b = Math.min(255, Math.round(rgb.b + (255 - rgb.b) * amount));

  return rgbToHex(r, g, b);
}

function darkenColor(hex, amount) {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;

  const r = Math.max(0, Math.round(rgb.r * (1 - amount)));
  const g = Math.max(0, Math.round(rgb.g * (1 - amount)));
  const b = Math.max(0, Math.round(rgb.b * (1 - amount)));

  return rgbToHex(r, g, b);
}

function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      }
    : null;
}

function rgbToHex(r, g, b) {
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}

export default createWidgetTheme;
